import React, { useState, useEffect } from 'react';
import { Typography, Paper, Box, Grid, TextField, Button, Switch, FormControlLabel, Snackbar, Alert, Container } from '@mui/material';
import { Code as CodeIcon, Settings as SettingsIcon } from '@mui/icons-material';
import MechanicsSettings from '../components/settings/MechanicsSettings';
import LaborSettings from '../components/settings/LaborSettings';
import WhatsAppSettings from '../components/settings/WhatsAppSettings';
import UsersManagement from '../components/settings/UsersManagement';
import DeveloperOptions from '../components/DeveloperOptions';
import { api } from '../utils/api';

const Settings = () => {
  const [companyInfo, setCompanyInfo] = useState({
    companyName: '',
    address: '',
    city: '',
    postalCode: '',
    province: '',
    phone: '',
    email: '',
    vatNumber: '',
    termsAndConditions: '',
    reviewLink: '',
    shippingName: '',
    shippingAddress: '',
    shippingCity: '',
    shippingPostalCode: '',
    shippingProvince: '',
    shippingPhone: ''
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [openDeveloper, setOpenDeveloper] = useState(false);

  useEffect(() => {
    fetchCompanyInfo();
    fetchCurrentUser();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setCurrentUser(response.data);
    } catch (err) {
      console.error('Error fetching current user:', err);
    }
  };

  const fetchCompanyInfo = async () => {
    setLoading(true);
    try {
      const res = await api.get('/settings');
      const newCompanyInfo = { ...companyInfo };
      
      // Map settings to company info fields
      Object.entries(res.data).forEach(([key, value]) => {
        switch (key) {
          case 'companyName':
            newCompanyInfo.companyName = value;
            break;
          case 'companyAddress':
            newCompanyInfo.address = value;
            break;
          case 'companyCity':
            newCompanyInfo.city = value;
            break;
          case 'companyPostalCode':
            newCompanyInfo.postalCode = value;
            break;
          case 'companyProvince':
            newCompanyInfo.province = value;
            break;
          case 'companyPhone':
            newCompanyInfo.phone = value;
            break;
          case 'companyEmail':
            newCompanyInfo.email = value;
            break;
          case 'companyVatNumber':
            newCompanyInfo.vatNumber = value;
            break;
          case 'termsAndConditions':
            newCompanyInfo.termsAndConditions = value;
            break;
          case 'reviewLink':
            newCompanyInfo.reviewLink = value;
            break;
          case 'shippingName':
            newCompanyInfo.shippingName = value;
            break;
          case 'shippingAddress':
            newCompanyInfo.shippingAddress = value;
            break;
          case 'shippingCity':
            newCompanyInfo.shippingCity = value;
            break;
          case 'shippingPostalCode':
            newCompanyInfo.shippingPostalCode = value;
            break;
          case 'shippingProvince':
            newCompanyInfo.shippingProvince = value;
            break;
          case 'shippingPhone':
            newCompanyInfo.shippingPhone = value;
            break;
        }
      });
      
      setCompanyInfo(newCompanyInfo);
    } catch (err) {
      console.error('Error fetching company info:', err);
      setSnackbar({
        open: true,
        message: 'Errore nel caricamento delle informazioni aziendali',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCompanyInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveCompanyInfo = async () => {
    setLoading(true);
    try {
      // Prepara tutte le impostazioni da salvare
      const settings = [
        { key: 'companyName', value: companyInfo.companyName, description: 'Nome dell\'azienda' },
        { key: 'companyAddress', value: companyInfo.address, description: 'Indirizzo dell\'azienda' },
        { key: 'companyCity', value: companyInfo.city, description: 'Città dell\'azienda' },
        { key: 'companyPostalCode', value: companyInfo.postalCode, description: 'CAP dell\'azienda' },
        { key: 'companyProvince', value: companyInfo.province, description: 'Provincia dell\'azienda' },
        { key: 'companyPhone', value: companyInfo.phone, description: 'Telefono dell\'azienda' },
        { key: 'companyEmail', value: companyInfo.email, description: 'Email dell\'azienda' },
        { key: 'companyVatNumber', value: companyInfo.vatNumber, description: 'Partita IVA dell\'azienda' },
        { key: 'termsAndConditions', value: companyInfo.termsAndConditions, description: 'URL dei termini e condizioni' },
        { key: 'reviewLink', value: companyInfo.reviewLink, description: 'URL pagina recensioni (Google, Trustpilot, ecc.)' },
        { key: 'shippingName', value: companyInfo.shippingName, description: 'Nome destinatario spedizione' },
        { key: 'shippingAddress', value: companyInfo.shippingAddress, description: 'Indirizzo di spedizione' },
        { key: 'shippingCity', value: companyInfo.shippingCity, description: 'Città di spedizione' },
        { key: 'shippingPostalCode', value: companyInfo.shippingPostalCode, description: 'CAP di spedizione' },
        { key: 'shippingProvince', value: companyInfo.shippingProvince, description: 'Provincia di spedizione' },
        { key: 'shippingPhone', value: companyInfo.shippingPhone, description: 'Telefono di spedizione' }
      ];

      // Salva tutte le impostazioni
      await Promise.all(settings.map(setting => 
        api.post('/settings', setting)
      ));
      
      setSnackbar({
        open: true,
        message: 'Informazioni aziendali salvate con successo',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error saving company info:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Errore durante il salvataggio delle informazioni aziendali',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Impostazioni
      </Typography>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Gestione Meccanici
        </Typography>
        <MechanicsSettings />
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Impostazioni Manodopera
        </Typography>
        <LaborSettings />
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Personalizzazione Messaggi WhatsApp
        </Typography>
        <WhatsAppSettings />
      </Box>

      {/* Strumenti Sviluppatore */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          <CodeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Strumenti Sviluppatore
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Accedi agli strumenti avanzati per integrare servizi esterni e personalizzare il sistema.
          </Typography>
          {!['admin', 'superadmin', 'supersaiyan'].includes(currentUser?.role) && (
            <Alert severity="info" sx={{ mt: 1 }}>
              Solo gli amministratori hanno accesso a queste funzionalità.
            </Alert>
          )}
        </Box>
        <Button
          variant="contained"
          onClick={() => setOpenDeveloper(true)}
          startIcon={<SettingsIcon />}
          disabled={!['admin', 'superadmin', 'supersaiyan'].includes(currentUser?.role)}
          sx={{
            background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
            color: 'white',
            boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
          }}
        >
          Apri Strumenti Sviluppatore
        </Button>
      </Box>

      <DeveloperOptions
        open={openDeveloper}
        onClose={() => setOpenDeveloper(false)}
      />

      <Grid container spacing={2}>
        {/* Users Management Section (solo per superadmin) */}
        {currentUser?.role === 'superadmin' && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <UsersManagement />
            </Paper>
          </Grid>
        )}
        
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Informazioni Azienda e Fatturazione
            </Typography>
            <Box component="form" sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Nome Azienda"
                name="companyName"
                value={companyInfo.companyName}
                onChange={handleInputChange}
                margin="normal"
                size="small"
              />
              <TextField
                fullWidth
                label="Indirizzo"
                name="address"
                value={companyInfo.address}
                onChange={handleInputChange}
                margin="normal"
                size="small"
              />
              <TextField
                fullWidth
                label="Città"
                name="city"
                value={companyInfo.city}
                onChange={handleInputChange}
                margin="normal"
                size="small"
              />
              <TextField
                fullWidth
                label="CAP"
                name="postalCode"
                value={companyInfo.postalCode}
                onChange={handleInputChange}
                margin="normal"
                size="small"
              />
              <TextField
                fullWidth
                label="Provincia"
                name="province"
                value={companyInfo.province}
                onChange={handleInputChange}
                margin="normal"
                size="small"
              />
              <TextField
                fullWidth
                label="Telefono"
                name="phone"
                value={companyInfo.phone}
                onChange={handleInputChange}
                margin="normal"
                size="small"
              />
              <TextField
                fullWidth
                label="Email"
                name="email"
                value={companyInfo.email}
                onChange={handleInputChange}
                margin="normal"
                size="small"
              />
              <TextField
                fullWidth
                label="Partita IVA"
                name="vatNumber"
                value={companyInfo.vatNumber}
                onChange={handleInputChange}
                margin="normal"
                size="small"
              />
              <TextField
                fullWidth
                label="Termini e condizioni"
                name="termsAndConditions"
                value={companyInfo.termsAndConditions}
                onChange={handleInputChange}
                placeholder="https://www.officinabici.it/termini-e-condizioni"
                margin="normal"
                size="small"
                helperText="Inserisci l'URL completo dei termini e condizioni che verrà utilizzato nei messaggi WhatsApp"
              />
              <TextField
                fullWidth
                label="Link recensioni"
                name="reviewLink"
                value={companyInfo.reviewLink}
                onChange={handleInputChange}
                placeholder="https://g.page/r/... o link alla scheda recensioni"
                margin="normal"
                size="small"
                helperText="URL usato nella variabile {reviewLink} nei messaggi WhatsApp (es. dopo consegna manutenzione)"
              />
            </Box>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Indirizzo di Spedizione
            </Typography>
            <Box component="form" sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Nome Destinatario"
                name="shippingName"
                value={companyInfo.shippingName}
                onChange={handleInputChange}
                margin="normal"
                size="small"
              />
              <TextField
                fullWidth
                label="Indirizzo di Spedizione"
                name="shippingAddress"
                value={companyInfo.shippingAddress}
                onChange={handleInputChange}
                margin="normal"
                size="small"
              />
              <TextField
                fullWidth
                label="Città di Spedizione"
                name="shippingCity"
                value={companyInfo.shippingCity}
                onChange={handleInputChange}
                margin="normal"
                size="small"
              />
              <TextField
                fullWidth
                label="CAP di Spedizione"
                name="shippingPostalCode"
                value={companyInfo.shippingPostalCode}
                onChange={handleInputChange}
                margin="normal"
                size="small"
              />
              <TextField
                fullWidth
                label="Provincia di Spedizione"
                name="shippingProvince"
                value={companyInfo.shippingProvince}
                onChange={handleInputChange}
                margin="normal"
                size="small"
              />
              <TextField
                fullWidth
                label="Telefono di Spedizione"
                name="shippingPhone"
                value={companyInfo.shippingPhone}
                onChange={handleInputChange}
                margin="normal"
                size="small"
              />
            </Box>
          </Paper>
        </Grid>

        {/* Pulsante Salva */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleSaveCompanyInfo}
              disabled={loading}
            >
              {loading ? 'Salvataggio...' : 'Salva Impostazioni'}
            </Button>
          </Box>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Settings;